import React from 'react';
import { formatInvoiceQuantity } from '../../../utils/invoiceFormat';

const money = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '0.00';
  return v.toFixed(2);
};

/**
 * Live + print-friendly invoice preview (no API calls).
 */
const InvoiceLivePreview = ({
  vendorLabel,
  vendorAddressLines = [],
  billToName,
  billToEmail,
  billToPhone,
  invoiceNumberPreview,
  invoiceDate,
  dueDate,
  lineItems,
  taxRate,
  subtotal,
  taxAmount,
  total,
  notes,
  paymentTerms,
  bookingRef,
  statusLabel,
}) => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden text-slate-900 p-8 sm:p-10">
      <div className="pb-7 sm:pb-8 border-b border-slate-100 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice</p>
          <h3 className="text-xl font-semibold text-slate-900 mt-1">{vendorLabel || 'Your business'}</h3>
          {vendorAddressLines.filter(Boolean).length > 0 && (
            <div className="mt-2 text-sm text-slate-600 space-y-0.5">
              {vendorAddressLines.filter(Boolean).map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          )}
        </div>
        <div className="text-left sm:text-right text-sm">
          <div className="text-slate-500">Invoice #</div>
          <div className="font-mono font-medium text-slate-900">{invoiceNumberPreview}</div>
          {statusLabel && (
            <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
              {statusLabel}
            </span>
          )}
        </div>
      </div>

      <div className="py-6 grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Bill to</p>
          <div className="text-sm space-y-1">
            <div className="font-medium text-slate-900">{billToName || '—'}</div>
            {billToEmail && <div className="text-slate-600">{billToEmail}</div>}
            {billToPhone && <div className="text-slate-600">{billToPhone}</div>}
          </div>
        </div>
        <div className="sm:text-right text-sm space-y-2">
          <div>
            <span className="text-slate-500">Invoice date </span>
            <span className="font-medium text-slate-900">{invoiceDate || '—'}</span>
          </div>
          <div>
            <span className="text-slate-500">Due date </span>
            <span className="font-medium text-slate-900">{dueDate || '—'}</span>
          </div>
          {bookingRef && (
            <div className="text-xs text-slate-500 pt-1">Linked booking: {bookingRef}</div>
          )}
        </div>
      </div>

      <div className="pb-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-3 pr-3 pl-0">Description</th>
              <th className="py-3 pr-3 w-20 text-right">Qty</th>
              <th className="py-3 pr-3 w-28 text-right">Rate</th>
              <th className="py-3 w-28 text-right pl-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(lineItems || []).map((row, idx) => (
              <tr key={idx} className="border-b border-slate-100">
                <td className="py-4 pr-3 pl-0 text-slate-800">{row.description || '—'}</td>
                <td className="py-4 pr-3 text-right tabular-nums">{formatInvoiceQuantity(row.quantity)}</td>
                <td className="py-4 pr-3 text-right tabular-nums">${money(row.unit_price)}</td>
                <td className="py-4 pl-2 text-right tabular-nums font-medium">
                  ${money(Number(row.quantity) * Number(row.unit_price))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="py-6 -mx-8 sm:-mx-10 px-8 sm:px-10 border-t border-slate-100 bg-slate-50">
        <div className="max-w-xs ml-auto space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Subtotal</span>
            <span className="tabular-nums font-medium">${money(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600">Tax {taxRate > 0 ? `(${taxRate}%)` : ''}</span>
            <span className="tabular-nums font-medium">${money(taxAmount)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-slate-200 text-base">
            <span className="font-semibold text-slate-900">Total</span>
            <span className="tabular-nums font-bold text-slate-900">${money(total)}</span>
          </div>
        </div>
      </div>

      {(paymentTerms || notes) && (
        <div className="pt-5 border-t border-slate-100 text-sm space-y-3">
          {paymentTerms && (
            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Payment terms</p>
              <p className="text-slate-700 whitespace-pre-wrap">{paymentTerms}</p>
            </div>
          )}
          {notes && (
            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Notes</p>
              <p className="text-slate-700 whitespace-pre-wrap">{notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceLivePreview;
